import {
  getAiInferencePresignedUrl,
  runAiInference,
} from '../../api/aiInference';
import { resolveRoomObjectImageSrcForDisplay } from '../roomObjectImage';
import { addTask } from '../../services/taskResolver';
import { extractTaskResultS3Key } from './photorealisticPromptInference';

export const RENDER_RESOLUTION_OPTIONS = [
  { id: '1k', label: '1K SD', apiValue: 1024 },
  { id: '2k', label: '2K Full HD', apiValue: 2048 },
  { id: '4k', label: '4K Ultra HD', apiValue: 4096 },
  { id: '8k', label: '8K Ultra HD', apiValue: 8192 },
];

/** Preview always uses the default 1K resolution regardless of Render resolution selection. */
export const DEFAULT_RENDER_PREVIEW_RESOLUTION_ID = '1k';

export const RENDER_LIGHTING_OPTIONS = [
  { id: 'default', label: 'Default', apiValue: null },
  { id: 'studio', label: 'Studio', apiValue: 'studio' },
  { id: 'golden_hour', label: 'Golden Hour', apiValue: 'golden_hour' },
  { id: 'daylight', label: 'Daylight', apiValue: 'daylight' },
  { id: 'night', label: 'Night', apiValue: 'night' },
];

const HORIZONTAL_LABELS = {
  0: 'Front',
  45: 'Front-right quarter',
  90: 'Right',
  135: 'Back-right quarter',
  180: 'Back',
  225: 'Back-left quarter',
  270: 'Left',
  315: 'Front-left quarter',
};

const VERTICAL_LABELS = {
  [-30]: 'Low-angle',
  0: 'Eye-level',
  30: 'Elevated',
  60: 'High-angle',
};

const DISTANCE_LABELS = {
  0.6: 'Close-up',
  1: 'Medium',
  1.0: 'Medium',
  1.8: 'Wide',
};

function normalizeRenderHistoryDistance(distance) {
  const value = Number(distance);
  if (value === 1.4) return 1.8;
  if (value === 0.6 || value === 1 || value === 1.8) return value;
  return 1;
}

export function resolveRenderResolutionApiValue(resolutionId) {
  const option = RENDER_RESOLUTION_OPTIONS.find((item) => item.id === resolutionId);
  return option?.apiValue ?? 1024;
}

export function resolveRenderLightingApiValue(lightingId) {
  if (lightingId === 'default') return null;
  const option = RENDER_LIGHTING_OPTIONS.find((item) => item.id === lightingId);
  return option?.apiValue ?? null;
}

export function formatRenderCameraLabel(cameraAngle = {}) {
  const azimuth = Number(cameraAngle.azimuth);
  const elevation = Number(cameraAngle.elevation);
  const distance = Number(cameraAngle.distance);
  const azimuthLabel = HORIZONTAL_LABELS[azimuth] ?? `${azimuth}°`;
  const elevationLabel = VERTICAL_LABELS[elevation] ?? `${elevation}°`;
  const distanceLabel = DISTANCE_LABELS[distance] ?? String(distance);
  return `${azimuthLabel}, ${elevationLabel}, ${distanceLabel}`;
}

export function buildRenderHistoryConfig({ resolutionId, lightingId, cameraAngle }) {
  const azimuth = Number(cameraAngle?.azimuth);
  const elevation = Number(cameraAngle?.elevation);
  const distance = normalizeRenderHistoryDistance(cameraAngle?.distance);

  return {
    taskName: 'room_image_render',
    resolution: resolveRenderResolutionApiValue(resolutionId),
    cameraAngle: {
      azimuth: Number.isFinite(azimuth) ? azimuth : 0,
      elevation: Number.isFinite(elevation) ? elevation : 0,
      distance,
    },
    lighting: resolveRenderLightingApiValue(lightingId),
  };
}

export function buildRenderHistoryRequestBody({
  resolutionId,
  lightingId,
  cameraAngle,
  resultS3Key,
}) {
  return {
    renderType: 'room_image',
    resultImageS3Key: resultS3Key,
    config: buildRenderHistoryConfig({ resolutionId, lightingId, cameraAngle }),
  };
}

function resolveResolutionIdFromApiValue(apiValue) {
  const numeric = Number(apiValue);
  const option = RENDER_RESOLUTION_OPTIONS.find((item) => item.apiValue === numeric);
  return option?.id ?? DEFAULT_RENDER_PREVIEW_RESOLUTION_ID;
}

function resolveLightingIdFromApiValue(apiValue) {
  if (apiValue == null || apiValue === '') return 'default';
  const option = RENDER_LIGHTING_OPTIONS.find((item) => item.apiValue === apiValue);
  return option?.id ?? 'default';
}

export function extractRenderSettingsFromLocalRenderItem(item) {
  if (item?.renderSettings && typeof item.renderSettings === 'object') {
    const { resolutionId, lightingId, cameraAngle } = item.renderSettings;
    if (resolutionId && lightingId && cameraAngle) {
      return {
        resolutionId,
        lightingId,
        cameraAngle: {
          azimuth: Number(cameraAngle?.azimuth) || 0,
          elevation: Number(cameraAngle?.elevation) || 0,
          distance: Number(cameraAngle?.distance) || 1,
        },
      };
    }
  }

  const payload = item?.payload;
  if (!payload || typeof payload !== 'object') return null;

  const config = payload.config && typeof payload.config === 'object' ? payload.config : payload;
  const resolution = config.resolution ?? payload.resolution;
  const lighting = config.lighting ?? payload.lighting;
  const cameraAngle = config.cameraAngle ?? config.camera_angle ?? payload.camera_angle;

  if (resolution == null || !cameraAngle) return null;

  return {
    resolutionId: resolveResolutionIdFromApiValue(resolution),
    lightingId: resolveLightingIdFromApiValue(lighting),
    cameraAngle: {
      azimuth: Number(cameraAngle?.azimuth) || 0,
      elevation: Number(cameraAngle?.elevation) || 0,
      distance: Number(cameraAngle?.distance) || 1,
    },
  };
}

export function formatInferenceStatusLabel(status) {
  const normalized = String(status ?? '').toLowerCase();
  if (normalized === 'pending' || normalized === 'queued' || normalized === 'queue') {
    return 'on queue';
  }
  if (
    normalized === 'started' ||
    normalized === 'processing' ||
    normalized === 'running'
  ) {
    return 'generating';
  }
  return null;
}

export function buildRoomImageRenderPayload(roomImageId, { resolutionId, lightingId, cameraAngle }) {
  const azimuth = Number(cameraAngle?.azimuth);
  const elevation = Number(cameraAngle?.elevation);
  const distance = Number(cameraAngle?.distance);

  return {
    task_name: 'room_image_render',
    room_image_id: Number(roomImageId),
    resolution: resolveRenderResolutionApiValue(resolutionId),
    camera_angle: {
      azimuth: Number.isFinite(azimuth) ? azimuth : 0,
      elevation: Number.isFinite(elevation) ? elevation : 0,
      distance: Number.isFinite(distance) ? distance : 1,
    },
    lighting: resolveRenderLightingApiValue(lightingId),
  };
}

/**
 * POST /ai-inference room_image_render, wait for completion, return preview image URL.
 * @param {number} roomImageId
 * @param {{ resolutionId: string, lightingId: string, cameraAngle: { azimuth: number, elevation: number, distance: number }, onStatusChange?: (status: string) => void }} options
 * @returns {Promise<{
 *   imageUrl: string,
 *   resultS3Key: string,
 *   payload: object,
 *   taskId?: string | null,
 *   completedAt?: string,
 *   inferenceId?: number | null,
 *   status?: string,
 * }>}
 */
export async function submitRoomImageRender(roomImageId, options) {
  const { onStatusChange, ...renderOptions } = options ?? {};
  const payload = buildRoomImageRenderPayload(roomImageId, renderOptions);
  console.log('POST /ai-inference room_image_render payload:', payload);

  const reportStatus = (status) => {
    const label = formatInferenceStatusLabel(status);
    if (label) onStatusChange?.(label);
  };

  const res = await runAiInference(payload);
  const immediateKey = extractTaskResultS3Key(res);
  const taskId = res?.taskId ?? res?.aiTaskId ?? res?.id;
  const initialStatus = res?.status ?? res?.taskStatus ?? res?.state ?? 'pending';
  reportStatus(initialStatus);

  const data =
    immediateKey != null
      ? res
      : taskId == null
        ? res
        : await new Promise((resolve, reject) => {
            addTask(taskId, {
              roomImageId,
              onResolve: resolve,
              onError: reject,
              onStatusChange: (status) => reportStatus(status),
            });
          });

  const resultS3Key = extractTaskResultS3Key(data);
  if (!resultS3Key) {
    throw new Error('Render completed without a result image.');
  }

  const presigned = await getAiInferencePresignedUrl(resultS3Key);
  const rawUrl = presigned?.url ?? presigned?.presignedUrl;
  if (!rawUrl) {
    throw new Error('Could not load the render preview image.');
  }

  const resolvedTaskId = data?.taskId ?? data?.task_id ?? taskId ?? null;
  const createdAt = data?.createdAt ?? data?.created_at ?? null;
  const startedAt = data?.startedAt ?? data?.started_at ?? null;
  const completedAt = data?.completedAt ?? data?.completed_at ?? null;
  const inferenceId = data?.id ?? data?.inferenceId ?? data?.inference_id ?? null;

  return {
    status: data?.status ?? data?.taskStatus ?? data?.state,
    resultS3Key,
    imageUrl: resolveRoomObjectImageSrcForDisplay(String(rawUrl).trim()),
    payload: data?.payload ?? payload,
    taskId: resolvedTaskId != null ? String(resolvedTaskId) : null,
    inferenceId: inferenceId != null ? String(inferenceId) : null,
    createdAt,
    startedAt,
    completedAt: completedAt ?? createdAt ?? new Date().toISOString(),
  };
}

/** @deprecated Use submitRoomImageRender */
export async function submitRoomImageRenderPreview(roomImageId, options) {
  return submitRoomImageRender(roomImageId, options);
}
